import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FaFileInvoice, FaCoins, FaHandHoldingUsd, FaChevronDown, FaNotesMedical } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { budgetApi } from '../../api/budget';
import { handleApiResponse } from '../global/ApiMessageModal/ApiMessageModal';

const OngezaBajetiModal = ({ show, onHide, onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  const [formData, setFormData] = useState({
    service_category_id: '',
    amount: '',
    amount_paid: '',
    description: ''
  });

  useEffect(() => {
    if (show) {
      fetchCategories();
      setFormData({
        service_category_id: '',
        amount: '',
        amount_paid: '',
        description: ''
      });
      setCategoryDropdownOpen(false);
    }
  }, [show]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await budgetApi.getServiceCategories();
      const apiPayload = response?.data || response;
      const dataWrapper = apiPayload?.data ?? apiPayload;
      const categoriesArray = dataWrapper?.data ?? dataWrapper;
      let normalizedCats = Array.isArray(categoriesArray) ? categoriesArray : [];
      if (normalizedCats.length > 0 && Array.isArray(normalizedCats[0])) {
        normalizedCats = normalizedCats.flat();
      }
      const finalCats = normalizedCats.map(cat => ({
        ...cat,
        name: cat.category || cat.name || 'Unknown'
      }));
      setCategories(finalCats);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
      toast.error('Imeshindwa kupakua makundi ya bajeti');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service_category_id) {
      toast.warning('Tafadhali chagua kundi la huduma');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.warning('Tafadhali ingiza kiasi sahihi cha bajeti');
      return;
    }

    setSubmitting(true);
    try {
      const initialPaid = parseFloat(formData.amount_paid) || 0;
      
      // Multi-key payload to ensure backend receives amount_paid regardless of key name requirement
      const payload = {
        service_category_id: parseInt(formData.service_category_id),
        amount: parseFloat(formData.amount),
        amount_paid: initialPaid,
        paid_amount: initialPaid,
        paid: initialPaid,
        new_payment: initialPaid,
        description: formData.description || null
      };

      const response = await budgetApi.createBudget(payload);
      if (handleApiResponse(response)) {
        toast.success('Kipengele cha bajeti kimehifadhiwa kikamilifu!');
        onHide();
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Error creating budget item:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategoryObj = categories.find(c => String(c.id) === String(formData.service_category_id));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold d-flex align-items-center gap-2" style={{ color: '#4169e1' }}>
          <div className="p-2 rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center">
            <FaFileInvoice size={22} />
          </div>
          Weka Kipengele Kipya cha Bajeti
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          {/* Category Dropdown */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Kundi la Huduma (Service Category) <span className="text-danger">*</span>
            </Form.Label>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setCategoryDropdownOpen(o => !o)}
                onBlur={() => setTimeout(() => setCategoryDropdownOpen(false), 150)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.6rem 0.9rem',
                  background: '#ffffff',
                  color: formData.service_category_id ? '#1f2937' : '#9ca3af',
                  fontWeight: formData.service_category_id ? '500' : '400',
                  fontSize: '0.92rem',
                  border: `2px solid ${categoryDropdownOpen ? '#4169e1' : '#d1d5db'}`,
                  borderRadius: '8px',
                  boxShadow: categoryDropdownOpen ? '0 0 0 3px rgba(65,105,225,0.18)' : 'none',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <span>{selectedCategoryObj ? selectedCategoryObj.name : 'Chagua Kundi la Huduma...'}</span>
                <FaChevronDown size={12} className="text-muted" />
              </button>

              {categoryDropdownOpen && (
                <div
                  className="bajeti-category-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    maxHeight: '220px',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    zIndex: 1050
                  }}
                >
                  {loadingCategories ? (
                    <div className="p-3 text-muted text-center small">
                      <Spinner animation="border" size="sm" className="me-2" /> Inapakia makundi...
                    </div>
                  ) : categories.length === 0 ? (
                    <div className="p-3 text-muted text-center small">Hakuna makundi yaliyopatikana</div>
                  ) : (
                    categories.map((cat) => (
                      <div
                        key={cat.id}
                        onMouseDown={() => {
                          setFormData({ ...formData, service_category_id: String(cat.id) });
                          setCategoryDropdownOpen(false);
                        }}
                        className={`p-2.5 px-3 border-bottom small cursor-pointer ${String(formData.service_category_id) === String(cat.id) ? 'bg-primary-subtle text-primary fw-bold' : ''}`}
                        style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      >
                        {cat.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </Form.Group>

          {/* Amount Estimated */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FaCoins className="me-2 text-muted" />
              Kiasi cha Bajeti (Budget Amount TZS) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="0.01"
              placeholder="Weka kiasi kilichokadiriwa cha bajeti (mfano: 500000)"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              className="rounded-3 shadow-none border py-2"
            />
          </Form.Group>

          {/* Amount Paid Initial */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FaHandHoldingUsd className="me-2 text-muted" />
              Kiasi Kilicholipwa (Amount Paid TZS - Si Lazima)
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              placeholder="Weka kiasi kilicholipwa tayari (mfano: 200000)"
              value={formData.amount_paid}
              onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
              className="rounded-3 shadow-none border py-2"
            />
            <Form.Text className="text-muted small">
              Kama tayari kuna kiasi kilicholipwa kama advancing au malipo ya awali, ingiza hapa.
            </Form.Text>
          </Form.Group>

          {/* Maelezo Description */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FaNotesMedical className="me-2 text-muted" />
              Maelezo (Description - Si Lazima)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Maelezo ya ziada kuhusu bajeti hii..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="rounded-3 shadow-none border"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 px-4 pb-4">
          <Button variant="light" onClick={onHide} className="rounded-pill px-4 fw-medium border">
            Ghairi
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            className="rounded-pill px-4 fw-semibold shadow-sm border-0"
            style={{ backgroundColor: '#4169e1' }}
          >
            {submitting ? (
              <><Spinner animation="border" size="sm" className="me-2" />Inahifadhi...</>
            ) : (
              'Hifadhi Bajeti'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default OngezaBajetiModal;
